import { computed, reactive, ref } from 'vue'
import * as api from './api'
import * as types from './types'
import { AgreementListFilterParameters } from '@/views/_shared/search-list/types'

export * from './types'

export const statusOptions = [
    {
        value: 'active',
        label: 'Active'
    },
    {
        value: 'cancelled',
        label: 'Cancelled'
    },
    {
        value: 'expired',
        label: 'Expired'
    },
    {
        value: 'draft',
        label: 'Draft'
    }
]
export const typeOptions = [
    {
        value: 'total',
        label: 'Total pricing'
    },
    {
        value: 'licence',
        label: 'Licence pricing'
    }
]

const agreements = ref<api.GetAgreementsListResponse['results']>([])
const agreementsCount = ref(0)
const createdAgreement = ref<{agreementId: string}>({ agreementId: '' })
const agreementsSimplified = ref<api.GetAgreementsSimplifiedListResponse>([])
const currentAgreement = ref<types.Agreement>({} as types.Agreement)
const agreementMembers = ref<api.GetAgreementMembersResponse>([])
const agreementAvailableMembers = ref<api.GetAgreementAvailableMembersResponse>([])
const agreementBillingInfo = ref<api.GetAgreementBillingInfoResponse>({} as api.GetAgreementBillingInfoResponse)
const agreementScheduledPrices = ref<api.GetAgreementScheduledPricesResponse>([])

const membersInAgreements = ref<api.GetMembersInAgreementsResponse['results']>([])
const membersInAgreementsCount = ref(0)
const membersInAgreementsSearchString = ref('')

const defaultSortBy = 'createdAt desc'

export const agreementsFilter = reactive<AgreementListFilterParameters>({
    search: {
        id: '',
        status: '',
        name: '',
        type: '',
        organization: '',
        dateFrom: '',
        dateTo: '',
        packageIds: [] as string[]
    },
    sortBy: null,
    limit: 10,
    offset: 0
})

const getAgreements = () =>
    api.getAgreements(
        agreementsFilter.search,
        agreementsFilter.sortBy || defaultSortBy,
        agreementsFilter.limit,
        agreementsFilter.offset
    ).then(
        agreementsList => {
            agreementsCount.value = agreementsList.count
            agreements.value = agreementsList.results
        }
    )

const getAgreementsSimplified = async () => {
    agreementsSimplified.value = await api.getAgreementsSimplifiedList()
}

const getAgreement = async (agreementId: string) => {
    currentAgreement.value = await api.getAgreement(agreementId)
    return currentAgreement.value
}

const removeDraftAgreement = async (agreementId: string) => {
    await api.removeDraftAgreement(agreementId)
}

const getAgreementBillingInfo = async (agreementId: string) => {
    agreementBillingInfo.value = await api.getAgreementBillingInfo(agreementId)
    return agreementBillingInfo.value
}

const getAgreementScheduledPrices = async (agreementId: string) => {
    currentAgreement.value.draft
        ? agreementScheduledPrices.value = await api.getDraftAgreementScheduledPrices(agreementId)
        : agreementScheduledPrices.value = await api.getAgreementScheduledPrices(agreementId)
    return agreementScheduledPrices.value
}

const createAgreementScheduledPrice = async (agreementId: string, payload: api.CreateAgreementScheduledPriceRequest) => {
    currentAgreement.value.draft
        ? await api.createDraftAgreementScheduledPrice(agreementId, payload)
        : await api.createAgreementScheduledPrice(agreementId, payload)
}

const updateAgreementScheduledPrice = async (agreementId: string, payload: api.UpdateAgreementScheduledPriceRequest) => {
    currentAgreement.value.draft
        ? await api.updateDraftAgreementScheduledPrice(agreementId, payload)
        : await api.updateAgreementScheduledPrice(agreementId, payload)
}

const deleteAgreementScheduledPrice = async (agreementId: string, payload: api.DeleteAgreementScheduledPriceRequest) => {
    currentAgreement.value.draft
        ? await api.deleteDraftAgreementScheduledPrice(agreementId, payload)
        : await api.deleteAgreementScheduledPrice(agreementId, payload)
}

const createAgreementContactInfo = async (agreementId: string, payload: api.CreateAgreementContactInfoRequest) => {
    currentAgreement.value.draft
        ? await api.createDraftAgreementContactInfo(agreementId, payload)
        : await api.createAgreementContactInfo(agreementId, payload)
}

const updateAgreementContactInfo = async (agreementId: string, payload: api.UpdateAgreementContactInfoRequest) => {
    currentAgreement.value.draft
        ? await api.updateDraftAgreementContactInfo(agreementId, payload)
        : await api.updateAgreementContactInfo(agreementId, payload)
}

const deleteAgreementContactInfo = async (agreementId: string, payload: api.DeleteAgreementContactInfoRequest) => {
    currentAgreement.value.draft
        ? await api.deleteDraftAgreementContactInfo(agreementId, payload)
        : await api.deleteAgreementContactInfo(agreementId, payload)
}

const addAgreement = async (draftAgreement: api.AddDraftAgreementRequest) => {
    createdAgreement.value = await api.addDraftAgreement(draftAgreement)
    await getAgreements()
}

const addAgreementWithEmailBillingInfo = async (draftAgreement: api.AddDraftAgreementRequest, billingInfo: types.AgreementBillingInfoEmailPayload) => {
    if (createdAgreement.value.agreementId === '') {
        createdAgreement.value = await api.addDraftAgreement(draftAgreement)
    }
    await api.changeAgreementBillingInfoEmail(createdAgreement.value.agreementId, { ...billingInfo, ...createdAgreement.value }).then(getAgreements)
}

const addAgreementWithMailBillingInfo = async (draftAgreement: api.AddDraftAgreementRequest, billingInfo: types.AgreementBillingInfoMailPayload) => {
    if (createdAgreement.value.agreementId === '') {
        createdAgreement.value = await api.addDraftAgreement(draftAgreement)
    }
    await api.changeAgreementBillingInfoMail(
        createdAgreement.value.agreementId,
        { ...billingInfo, ...createdAgreement.value }
    ).then(getAgreements)
}

const addAgreementWithElectronicBillingInfo = async (draftAgreement: api.AddDraftAgreementRequest, billingInfo: types.AgreementBillingInfoElectronicPayload) => {
    if (createdAgreement.value.agreementId === '') {
        createdAgreement.value = await api.addDraftAgreement(draftAgreement)
    }
    await api.changeAgreementBillingInfoElectronic(createdAgreement.value.agreementId, { ...billingInfo, ...createdAgreement.value }).then(getAgreements)
}

const getAgreementMembers = (id: string) => api.getAgreementMembers(id).then(agreementMembersList => {
    agreementMembers.value = agreementMembersList
})

const getAgreementAvailableMembers = (id: string) => api.getAgreementAvailableMembers(id).then(agreementAvailableMembersList => {
    agreementAvailableMembers.value = agreementAvailableMembersList
})

const addMemberToAgreement = async (agreementId: string, memberId: string) => {
    currentAgreement.value.draft
        ? await api.addMemberToDraftAgreement(agreementId, memberId)
        : await api.addMemberToAgreement(agreementId, memberId)
            .then(() => getAgreementMembers(agreementId))
}

const cancelAgreementMember = async (agreementId: string, payload: api.CancelMemberRequest) => {
    currentAgreement.value.draft
        ? await api.cancelDraftAgreementMember(agreementId, payload)
        : await api.cancelAgreementMember(agreementId, payload)
}

const withAgreementUpdate = async (agreementId: string, callback: () => void) => {
    await callback()
    await getAgreement(agreementId)
}

const disableAgreementForm = async (agreementId: string) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.disableDraftAgreementForm(agreementId)
            : () => api.disableAgreementForm(agreementId)
    )
}

const enableAgreementForm = async (agreementId: string) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.enableDraftAgreementForm(agreementId)
            : () => api.enableAgreementForm(agreementId)
    )
}

const updateAgreementEndDate = async (agreementId: string, endDate: string) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.updateDraftAgreementEndDate(agreementId, endDate)
            : () => api.updateAgreementEndDate(agreementId, endDate)
    )
}

const updateAgreementInvoicingAllowedFrom = async (agreementId: string, invoicingAllowedFrom: string) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.updateDraftAgreementInvoicingAllowedFrom(agreementId, invoicingAllowedFrom)
            : () => api.updateAgreementInvoicingAllowedFrom(agreementId, invoicingAllowedFrom)
    )
}

const updateAgreementMaxMembers = async (agreementId: string, maxMembers: number) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.updateDraftAgreementMaxMembers(agreementId, maxMembers)
            : () => api.updateAgreementMaxMembers(agreementId, maxMembers)
    )
}

const updateAgreementName = async (agreementId: string, name: string) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.updateDraftAgreementName(agreementId, name)
            : () => api.updateAgreementName(agreementId, name)
    )
}

const updateAgreementPackages = async (agreementId: string, packages: string[]) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.updateDraftAgreementPackages(agreementId, packages)
            : () => api.updateAgreementPackages(agreementId, packages)
    )
}

const updateAgreementPrice = async (agreementId: string, price: number) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.updateDraftAgreementPrice(agreementId, price)
            : () => api.updateAgreementPrice(agreementId, price)
    )
}

const updateAgreementAdditionalInfo = async (agreementId: string, additionalInfo: string | null) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.updateDraftAgreementAdditionalInfo(agreementId, additionalInfo)
            : () => api.updateAgreementAdditionalInfo(agreementId, additionalInfo)
    )
}

const denyAgreementMarketingCommunication = async (agreementId: string) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.denyDraftAgreementMarketingCommunication(agreementId)
            : () => api.denyAgreementMarketingCommunication(agreementId)
    )
}

const allowAgreementMarketingCommunication = async (agreementId: string) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.allowDraftAgreementMarketingCommunication(agreementId)
            : () => api.allowAgreementMarketingCommunication(agreementId)
    )
}

const disableDigitalClassroomsExclusion = async (agreementId: string) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.disableDraftDigitalClassroomsExclusion(agreementId)
            : () => api.disableDigitalClassroomsExclusion(agreementId)
    )
}

const enableDigitalClassroomsExclusion = async (agreementId: string) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.enableDraftDigitalClassroomsExclusion(agreementId)
            : () => api.enableDigitalClassroomsExclusion(agreementId)
    )
}

const enableLSPExport = async (agreementId: string) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.enableDraftAllowLSPExport(agreementId)
            : () => api.enableAllowLSPExport(agreementId)
    )
}

const disableLSPExport = async (agreementId: string) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.disableDraftAllowLSPExport(agreementId)
            : () => api.disableAllowLSPExport(agreementId)

    )
}

const enableEditInSelfService = async (agreementId: string) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.enableDraftEditInSelfService(agreementId)
            : () => api.enableEditInSelfService(agreementId)
    )
}

const disableEditInSelfService = async (agreementId: string) => {
    await withAgreementUpdate(agreementId,
        currentAgreement.value.draft
            ? () => api.disableDraftEditInSelfService(agreementId)
            : () => api.disableEditInSelfService(agreementId)

    )
}

const markAsReadyForPublication = async (agreementId: string, readyForPublication: boolean) =>
    await withAgreementUpdate(agreementId,
        () => api.markAsReadyForPublication(agreementId, readyForPublication)
    )

const withAgreementBillingInfoUpdate = async (agreementId: string, callback: () => void) => {
    await callback()
    await getAgreementBillingInfo(agreementId)
}

const updateBillingInfoOrganizationName = (agreementId: string, billingInfoId: string, payload: api.UpdateBillingInfoOrganizationNameRequest) =>
    withAgreementBillingInfoUpdate(agreementId,
        () => api.updateBillingInfoOrganizationName(agreementId, billingInfoId, payload)
    )

const updateBillingInfoBusinessId = (agreementId: string, billingInfoId: string, payload: api.UpdateBillingInfoBusinessIdRequest) =>
    withAgreementBillingInfoUpdate(agreementId,
        () => api.updateBillingInfoBusinessId(agreementId, billingInfoId, payload)
    )

const updateBillingInfoPaymentTerms = (agreementId: string, billingInfoId: string, payload: api.UpdateBillingInfoPaymentTermsRequest) =>
    withAgreementBillingInfoUpdate(agreementId,
        () => api.updateBillingInfoPaymentTerms(agreementId, billingInfoId, payload)
    )

const updateBillingInfoContactData = (agreementId: string, billingInfoId: string, payload: api.UpdateBillingInfoContactDataRequest) =>
    withAgreementBillingInfoUpdate(agreementId,
        () => api.updateBillingInfoContactData(agreementId, billingInfoId, payload)
    )

const updateBillingInfoAddress = (agreementId: string, billingInfoId: string, payload: api.UpdateBillingInfoAddressRequest) =>
    withAgreementBillingInfoUpdate(agreementId,
        () => api.updateBillingInfoAddress(agreementId, billingInfoId, payload)
    )

const updateBillingInfoInvoiceDescription = (agreementId: string, billingInfoId: string, payload: api.UpdateBillingInfoInvoiceDescriptionRequest) =>
    withAgreementBillingInfoUpdate(agreementId,
        () => api.updateBillingInfoInvoiceDescription(agreementId, billingInfoId, payload)
    )

const updateBillingInfoInvoiceReference = (agreementId: string, billingInfoId: string, payload: api.UpdateBillingInfoInvoiceReferenceRequest) =>
    withAgreementBillingInfoUpdate(agreementId,
        () => api.updateBillingInfoInvoiceReference(agreementId, billingInfoId, payload))

const updateBillingInfoVatId = (agreementId: string, billingInfoId: string, payload: api.UpdateBillingInfoVatIdRequest) =>
    withAgreementBillingInfoUpdate(agreementId, () => api.updateBillingInfoVatId(agreementId, billingInfoId, payload))

const updateBillingInfoCustomerNumber = (agreementId: string, billingInfoId: string, payload: api.UpdateBillingInfoCustomerNumberRequest) =>
    withAgreementBillingInfoUpdate(agreementId, () => api.updateBillingInfoCustomerNumber(agreementId, billingInfoId, payload))

const assignElectronicDeliveryPolicy = (agreementId: string, billingInfoId: string, payload: api.AssignElectronicDeliveryPolicyRequest) =>
    withAgreementBillingInfoUpdate(agreementId,
        () => api.assignElectronicDeliveryPolicy(agreementId, billingInfoId, payload)
    )

const assignEmailDeliveryPolicy = (agreementId: string, billingInfoId: string, payload: api.AssignEmailDeliveryPolicyRequest) =>
    withAgreementBillingInfoUpdate(agreementId,
        () => api.assignEmailDeliveryPolicy(agreementId, billingInfoId, payload)
    )

const assignMailDeliveryPolicy = (agreementId: string, billingInfoId: string, payload: api.AssignMailDeliveryPolicyRequest) =>
    withAgreementBillingInfoUpdate(agreementId,
        () => api.assignMailDeliveryPolicy(agreementId, billingInfoId, payload)
    )

const getMembersInAgreements = (searchString: string) =>
    api.getMembersInAgreements(searchString).then(
        membersInAgreementsList => {
            membersInAgreementsCount.value = membersInAgreementsList.count
            membersInAgreementsSearchString.value = searchString
            membersInAgreements.value = membersInAgreementsList.results
        }
    )

export function useAgreementModule () {
    return {
        addAgreement,
        addAgreementWithEmailBillingInfo,
        addAgreementWithMailBillingInfo,
        addAgreementWithElectronicBillingInfo,
        agreementMembers: computed(() => agreementMembers.value),
        agreementAvailableMembers: computed(() => agreementAvailableMembers.value),
        agreements: computed(() => agreements.value),
        agreementsCount: computed(() => agreementsCount.value),
        agreementsFilter,
        cancelAgreement: api.cancelAgreement,
        createdAgreement,
        getAgreement,
        getAgreementsSimplified,
        agreementsSimplified: computed(() => agreementsSimplified.value),
        agreementsSimplifiedById: computed(() => agreementsSimplified.value.reduce((list, agreement) => {
            list.set(agreement.agreementId, agreement)
            return list
        }, new Map<string, types.AgreementsSimplifiedListModel>())),
        getAgreementMembers,
        getAgreementAvailableMembers,
        getAgreements,
        getAgreementBillingInfo,
        agreementBillingInfo: computed(() => agreementBillingInfo.value),
        currentAgreement: computed(() => currentAgreement.value),
        purgeAgreementMember: api.purgeAgreementMember,
        swapAgreementMember: api.swapAgreementMember,
        resetAgreementMemberAccess: api.resetAgreementMemberAccess,
        getAgreementScheduledPrices,
        agreementScheduledPrices: computed(() => agreementScheduledPrices.value.sort(
            (pa, pb) => pa.validFrom.localeCompare(pb.validFrom))
        ),
        createAgreementScheduledPrice,
        updateAgreementScheduledPrice,
        deleteAgreementScheduledPrice,
        cancelAgreementMember,
        updateAgreementEndDate,
        updateAgreementInvoicingAllowedFrom,
        updateAgreementMaxMembers,
        updateAgreementName,
        updateAgreementPackages,
        updateAgreementPrice,
        updateAgreementAdditionalInfo,
        disableAgreementForm,
        enableAgreementForm,
        addMemberToAgreement,
        updateBillingInfoOrganizationName,
        updateBillingInfoBusinessId,
        updateBillingInfoPaymentTerms,
        updateBillingInfoContactData,
        updateBillingInfoAddress,
        updateBillingInfoInvoiceDescription,
        updateBillingInfoInvoiceReference,
        updateBillingInfoVatId,
        updateBillingInfoCustomerNumber,
        assignElectronicDeliveryPolicy,
        assignEmailDeliveryPolicy,
        assignMailDeliveryPolicy,
        denyAgreementMarketingCommunication,
        allowAgreementMarketingCommunication,
        disableDigitalClassroomsExclusion,
        enableDigitalClassroomsExclusion,
        disableLSPExport,
        enableLSPExport,
        enableEditInSelfService,
        disableEditInSelfService,
        markAsReadyForPublication,
        removeDraftAgreement,
        getMembersInAgreements,
        createAgreementContactInfo,
        updateAgreementContactInfo,
        deleteAgreementContactInfo,
        membersInAgreementsSearchString,
        membersInAgreements: computed(() => membersInAgreements.value)
    }
}
